import { BILLING_PLANS, setBillingPlan } from "../client/dashboard/billingPlanStorage";

/**
 * Plan model:
 *   trial      — 3-day free trial (all features, unlimited locations)
 *   basic      — €79/month, Basic-level features, 1 connected location
 *   premium    — €179/month, full feature set, 1 connected location
 *   enterprise — individual pricing (sales-assigned): Premium + unlimited locations
 *   expired    — trial over / subscription cancelled; locked to billing pages
 */
export const ACCOUNT_STATUS = {
  TRIAL: "trial",
  BASIC: "basic",
  PREMIUM: "premium",
  ENTERPRISE: "enterprise",
  EXPIRED: "expired",
  // Legacy values (pre-migration payloads) — normalized below.
  PAID: "paid",
  UNPAID: "unpaid",
};

/** Map legacy server values onto the new plan model. */
export function normalizeAccountStatus(status) {
  if (status === ACCOUNT_STATUS.PAID) return ACCOUNT_STATUS.PREMIUM;
  if (status === ACCOUNT_STATUS.UNPAID) return ACCOUNT_STATUS.TRIAL;
  return status || ACCOUNT_STATUS.TRIAL;
}

/** Premium-level plan (Premium or Enterprise): perks, free deletions, subscriber pricing. */
export function isPremiumAccount(status) {
  const s = normalizeAccountStatus(status);
  return s === ACCOUNT_STATUS.PREMIUM || s === ACCOUNT_STATUS.ENTERPRISE;
}

/** Enterprise plan: everything in Premium plus unlimited connected locations. */
export function isEnterpriseAccount(status) {
  return normalizeAccountStatus(status) === ACCOUNT_STATUS.ENTERPRISE;
}

/** Any active paid subscription (basic, premium or enterprise). */
export function isPaidAccount(status) {
  const s = normalizeAccountStatus(status);
  return (
    s === ACCOUNT_STATUS.BASIC ||
    s === ACCOUNT_STATUS.PREMIUM ||
    s === ACCOUNT_STATUS.ENTERPRISE
  );
}

/** May use the app at all (everything except expired). */
export function hasActiveAccess(status) {
  return normalizeAccountStatus(status) !== ACCOUNT_STATUS.EXPIRED;
}

export function isExpiredAccount(status) {
  return normalizeAccountStatus(status) === ACCOUNT_STATUS.EXPIRED;
}

/** Deletion-case pricing: only Premium gets the subscriber tier. */
export function billingPlanForAccountStatus(status) {
  return isPremiumAccount(status) ? BILLING_PLANS.SUBSCRIBER : BILLING_PLANS.FREE;
}

/** Apply server account status to session + billing plan (call after login/profile). */
export function applyAccountStatusToSession(user) {
  if (!user?.account_status) return;
  const plan = billingPlanForAccountStatus(user.account_status);
  setBillingPlan(plan);
  return user.account_status;
}

export function accountStatusLabel(status, t) {
  const s = normalizeAccountStatus(status);
  if (t) {
    if (s === ACCOUNT_STATUS.ENTERPRISE) return t("account.enterprise");
    if (s === ACCOUNT_STATUS.PREMIUM) return t("account.premium");
    if (s === ACCOUNT_STATUS.BASIC) return t("account.basic");
    if (s === ACCOUNT_STATUS.EXPIRED) return t("account.expired");
    return t("account.trial");
  }
  if (s === ACCOUNT_STATUS.ENTERPRISE) return "Enterprise";
  if (s === ACCOUNT_STATUS.PREMIUM) return "Premium";
  if (s === ACCOUNT_STATUS.BASIC) return "Basic";
  if (s === ACCOUNT_STATUS.EXPIRED) return "Expired";
  return "Trial";
}

/** Whole days of trial remaining (0 when over or not set). */
export function trialDaysLeft(trialEndsAt) {
  if (!trialEndsAt) return 0;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return 0;
  const msLeft = end - Date.now();
  if (msLeft <= 0) return 0;
  return Math.ceil(msLeft / (24 * 60 * 60 * 1000));
}

/** True when the trial clock has already passed (regardless of account_status). */
export function isTrialClockExpired(trialEndsAt) {
  if (!trialEndsAt) return false;
  const end = new Date(trialEndsAt).getTime();
  if (Number.isNaN(end)) return false;
  return end <= Date.now();
}

/** Locale-aware date + time for trial expiry banners. */
export function formatTrialEndsAt(trialEndsAt, locale = "en") {
  if (!trialEndsAt) return { date: "", time: "" };
  const d = new Date(trialEndsAt);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const loc = locale === "de" ? "de-DE" : "en-GB";
  return {
    date: d.toLocaleDateString(loc, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    time: d.toLocaleTimeString(loc, {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}
